import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { onAuthStateChanged, signOut, signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../../firebase';
import ApiService from '../../services/api.service';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser]   = useState(null);
  const [loading, setLoading] = useState(true);
  // userInfo is the backend Users row (from /me). Holds phoneVerified, role,
  // businesses, etc. Null while still loading or if /me hasn't returned yet.
  const [userInfo, setUserInfo] = useState(null);
  const [userInfoLoading, setUserInfoLoading] = useState(false);
  // True when /me returned 404 for the authenticated Firebase user — i.e.
  // Firebase auth succeeded but the backend Users row doesn't exist yet
  // (typically an abandoned Google/Apple signup). PrivateRoute reads this to
  // funnel the user into /signup?from=login. Transient errors leave this
  // false so a network blip doesn't bounce users out of the app.
  const [userMissing, setUserMissing] = useState(false);

  const refreshUserInfo = useCallback(async () => {
    if (!auth.currentUser) {
      setUserInfo(null);
      setUserMissing(false);
      return null;
    }
    setUserInfoLoading(true);
    try {
      const me = await ApiService.getMe();
      setUserInfo(me || null);
      setUserMissing(false);
      return me;
    } catch (err) {
      const is404 = /\b404\b/.test(err?.message || '');
      if (!is404) console.warn('AuthContext /me fetch failed:', err?.message);
      setUserInfo(null);
      setUserMissing(is404);
      return null;
    } finally {
      setUserInfoLoading(false);
    }
  }, []);

  useEffect(() => {
    // Listen once globally for Firebase auth state
    const unsub = onAuthStateChanged(auth, async (u) => {
      setUser(u || null);
      setLoading(false);
      if (u) {
        await refreshUserInfo();
      } else {
        setUserInfo(null);
        setUserMissing(false);
      }
    });
    return () => unsub();
  }, [refreshUserInfo]);

  const value = useMemo(() => ({
    user,
    userInfo,
    userInfoLoading,
    userMissing,
    refreshUserInfo,
    loading,
    isAuthenticated: !!user,
    // handy helpers so components/pages can use the context directly
    signIn: (email, password) => signInWithEmailAndPassword(auth, email, password),
    signOut: () => signOut(auth),
    getIdToken: async () => (auth.currentUser ? auth.currentUser.getIdToken(true) : null),
  }), [user, userInfo, userInfoLoading, userMissing, refreshUserInfo, loading]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
